import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchFilter from '../components/SearchFilter';
import { CardActionsPlaceholder } from '../components/UserInteractionPlaceholders';
import { useLanguage } from '../context/LanguageContext';
import { moviesApi, reviewsApi, languageToTMDB, getGenreNames } from '../services/api';
import { Film, Star, Sparkles, FileText, Eye, Camera, ArrowLeft, Calendar } from 'lucide-react';

const categoryIcons = {
  newReleases: Film,
  classics: Star,
  cult: Sparkles,
  documentaries: FileText,
  hiddenGems: Eye,
  directors: Camera,
};

const categoryApiMap = {
  newReleases: 'getNowPlaying',
  classics: 'getTopRated',
  cult: 'getCultFilms',
  documentaries: 'getDocumentaries',
  hiddenGems: 'getUpcoming',
  directors: 'getTopRated',
};

const CategoriesPage = () => {
  const { category } = useParams();
  const { t, language } = useLanguage();
  const [movies, setMovies] = useState([]);
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [categoryReviews, setCategoryReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { key: 'newReleases', label: t('categories.newReleases'), description: 'Latest theatrical releases and new films' },
    { key: 'classics', label: t('categories.classics'), description: 'Timeless masterpieces that shaped cinema' },
    { key: 'cult', label: t('categories.cult'), description: 'Underground favorites with devoted followings' },
    { key: 'documentaries', label: t('categories.documentaries'), description: 'Non-fiction explorations of reality' },
    { key: 'hiddenGems', label: t('categories.hiddenGems'), description: 'Overlooked films deserving attention' },
    { key: 'directors', label: t('categories.directors'), description: 'Spotlight on auteur filmmakers' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!category) {
        try {
          const reviews = await reviewsApi.getAll(null, 100);
          setCategoryReviews(reviews);
        } catch (err) {
          console.error('Error fetching reviews:', err);
        }
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const tmdbLang = languageToTMDB[language] || 'en-US';
        const apiMethod = categoryApiMap[category] || 'getPopular';
        
        const data = await moviesApi[apiMethod](1, tmdbLang);
        setMovies(data.results.slice(0, 12));
        setFilteredMovies(data.results.slice(0, 12));
      } catch (error) {
        console.error('Error fetching movies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [category, language]);

  const currentCategory = categories.find((c) => c.key === category);

  const getReviewCount = (catKey) => {
    return categoryReviews.filter((r) => r.category === catKey).length;
  };

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query) {
      setFilteredMovies(movies);
    } else {
      setFilteredMovies(
        movies.filter(m => m.title.toLowerCase().includes(query.toLowerCase()))
      );
    }
  };

  // Handle filter changes
  const handleFilterChange = (filters) => {
    let result = [...movies];

    if (searchQuery) {
      result = result.filter(m => m.title.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    if (filters.sort) {
      switch (filters.sort) {
        case 'title':
          result.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'release_date':
          result.sort((a, b) => (b.year || '').localeCompare(a.year || ''));
          break;
        case 'popularity':
        default:
          break;
      }
    }

    setFilteredMovies(result);
  };

  if (loading) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20 flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#52ea88] border-t-transparent animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Page Header */}
          {category ? (
            <div className="space-y-4 mb-8">
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors text-sm mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                All Categories
              </Link>
              <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6]">
                {currentCategory?.label || category}
              </h1>
              {currentCategory?.description && (
                <p className="text-[#9FBFB4] text-lg">{currentCategory.description}</p>
              )}
              <div className="w-20 h-1 bg-[#52ea88]"></div>
            </div>
          ) : (
            <div className="space-y-4 mb-12">
              <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6]">
                {t('sections.categories')}
              </h1>
              <p className="text-[#9FBFB4] text-lg max-w-2xl">
                Explore our curated collections of films organized by theme, era, and style.
              </p>
              <div className="w-20 h-1 bg-[#52ea88]"></div>
            </div>
          )}

          {/* Search & Filter (only when viewing a category) */}
          {category && (
            <SearchFilter 
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              showCategories={false}
              showGenres={false}
              showYears={false}
            />
          )}

          {/* Category Grid (if no category selected) */}
          {!category && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {categories.map((cat) => {
                const Icon = categoryIcons[cat.key] || Film;
                const count = getReviewCount(cat.key);

                return (
                  <Link
                    key={cat.key}
                    to={`/categories/${cat.key}`}
                    className="group relative overflow-hidden border border-[#52ea88]/10 hover:border-[#52ea88]/40 transition-all duration-300 p-8 bg-[#121917]"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#52ea88]/3 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <div className="relative z-10 space-y-4">
                      <div className="w-14 h-14 border border-[#52ea88]/20 flex items-center justify-center group-hover:border-[#52ea88]/50 transition-colors">
                        <Icon className="w-6 h-6 text-[#52ea88]" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors">
                        {cat.label}
                      </h3>
                      <p className="text-[#9FBFB4] text-sm line-clamp-2">
                        {cat.description}
                      </p>
                      <p className="text-[#9FBFB4]/70 text-sm">
                        {count > 0 ? `${count} reviews` : 'Browse films'}
                      </p>
                      <div className="w-8 h-0.5 bg-[#9FBFB4]/20 group-hover:bg-[#52ea88] group-hover:w-12 transition-all duration-300" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Movies in Category */}
          {category && filteredMovies.length > 0 && (
            <>
              <p className="text-[#9FBFB4] text-sm mb-6">
                Showing {filteredMovies.length} films
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredMovies.map((movie) => {
                  const genres = getGenreNames(movie.genre_ids || []);

                  return (
                    <Link
                      key={movie.id}
                      to={`/movie/${movie.id}`}
                      className="group relative block bg-[#121917] border border-[#52ea88]/10 overflow-hidden hover:border-[#52ea88]/40 transition-all duration-300"
                    >
                      {/* Card Actions Placeholder */}
                      <CardActionsPlaceholder />

                      <div className="relative aspect-[2/3] overflow-hidden">
                        {movie.poster ? (
                          <img
                            src={movie.poster}
                            alt={movie.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#0B0F0E] flex items-center justify-center">
                            <span className="text-[#9FBFB4]/50">No Image</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#121917] via-transparent to-transparent" />
                        
                        {/* Genre Tags */}
                        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                          {genres.slice(0, 2).map((g) => (
                            <span
                              key={g}
                              className="px-2 py-1 bg-[#0B0F0E]/80 backdrop-blur-sm text-[#52ea88] text-xs font-medium"
                            >
                              {g}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="p-5 space-y-3">
                        <h3 className="text-lg font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors line-clamp-1">
                          {movie.title}
                        </h3>
                        <div className="flex items-center gap-4 text-[#9FBFB4] text-sm">
                          {movie.year && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {movie.year}
                            </span>
                          )}
                          {movie.vote_average > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              {movie.vote_average.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {category && filteredMovies.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#9FBFB4]/70 text-lg">No films match your search.</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilteredMovies(movies);
                }}
                className="text-[#52ea88] hover:underline mt-2"
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
